import React from "react";
import {
    Box,
    Typography,
    Tooltip,
    Stack,
    Chip,
    Button,
    Menu,
    MenuItem,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {
    LocationOn as LocationIcon,
    CalendarToday as CalendarIcon,
    PeopleAlt as PeopleAltIcon,
    ArrowOutward as ArrowOutwardIcon,
    BusinessCenter as BusinessCenterIcon,
    LockOutlined as LockIcon,
    KeyboardArrowDown as DropdownIcon,
    Quiz as QuizIcon,
    VisibilityOutlined as VisibilityOutlinedIcon,
} from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";

const STATUS_OPTIONS = ["open", "onhold", "closed"];

export const useTooltipProps = (theme) => ({
    arrow: true,
    enterDelay: 200,
    leaveDelay: 100,
    componentsProps: {
        tooltip: {
            sx: {
                bgcolor: theme.palette.text.primary,
                color: theme.palette.background.paper,
                fontWeight: 600,
                fontSize: "0.7rem",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                p: "8px 12px",
                borderRadius: "8px",
                boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                letterSpacing: "0.01em",
            },
        },
        arrow: {
            sx: {
                color: theme.palette.text.primary,
            }
        }
    },
});

export const useActionButtonStyles = (theme) => {
    const applyBtnSx = {
        flex: 1,
        height: 34,
        borderRadius: "10px",
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        color: theme.palette.primary.main,
        fontWeight: 700,
        fontSize: "0.72rem",
        textTransform: "none",
        fontFamily: "inherit",
        border: `1.5px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        "&:hover": {
            bgcolor: theme.palette.primary.main,
            color: "#fff",
            borderColor: theme.palette.primary.main,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
            transform: "translateY(-1px)",
        },
        "&:active": {
            transform: "translateY(1px)",
        }
    };

    const matchBtnSx = {
        flex: 1,
        height: 34,
        borderRadius: "10px",
        fontWeight: 700,
        fontSize: "0.72rem",
        textTransform: "none",
        fontFamily: "inherit",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        bgcolor: alpha(theme.palette.info.main, 0.08),
        color: theme.palette.info.main,
        border: `1.5px solid ${alpha(theme.palette.info.main, 0.15)}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        "&:hover": {
            bgcolor: theme.palette.info.main,
            color: "#fff",
            borderColor: theme.palette.info.main,
            boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.25)}`,
            transform: "translateY(-1px)",
        },
        "&:active": {
            transform: "translateY(1px)",
        },
        "&.Mui-disabled": {
            bgcolor: alpha(theme.palette.action.disabledBackground, 0.04),
            color: alpha(theme.palette.text.secondary, 0.35),
            border: `1.5px dashed ${alpha(theme.palette.divider, 0.6)}`,
            boxShadow: "none",
            transform: "none",
        },
    };

    const skillsBtnSx = {
        flex: 1,
        height: 34,
        borderRadius: "10px",
        fontWeight: 700,
        fontSize: "0.72rem",
        textTransform: "none",
        fontFamily: "inherit",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        bgcolor: alpha(theme.palette.warning.main, 0.08),
        color: theme.palette.warning.dark,
        border: `1.5px solid ${alpha(theme.palette.warning.main, 0.15)}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        "&:hover": {
            bgcolor: theme.palette.warning.main,
            color: "#fff",
            borderColor: theme.palette.warning.main,
            boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.25)}`,
            transform: "translateY(-1px)",
        },
        "&:active": {
            transform: "translateY(1px)",
        },
        "&.Mui-disabled": {
            bgcolor: alpha(theme.palette.action.disabledBackground, 0.04),
            color: alpha(theme.palette.text.secondary, 0.35),
            border: `1.5px dashed ${alpha(theme.palette.divider, 0.6)}`,
            boxShadow: "none",
            transform: "none",
        },
    };

    const questionsBtnSx = {
        flex: 1,
        height: 34,
        borderRadius: "10px",
        fontWeight: 700,
        fontSize: "0.72rem",
        textTransform: "none",
        fontFamily: "inherit",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        bgcolor: alpha(theme.palette.success.main, 0.08),
        color: theme.palette.success.main,
        border: `1.5px solid ${alpha(theme.palette.success.main, 0.15)}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        "&:hover": {
            bgcolor: theme.palette.success.main,
            color: "#fff",
            borderColor: theme.palette.success.main,
            transform: "translateY(-1px)",
        },
    };

    return { applyBtnSx, matchBtnSx, skillsBtnSx, questionsBtnSx };
};

export const useStatusStyle = (theme) => (status) => {
    switch (status?.toLowerCase()) {
        case "active":
        case "open":
            return {
                bg: alpha(theme.palette.success.main, 0.08),
                color: theme.palette.success.dark || theme.palette.success.main,
                border: alpha(theme.palette.success.main, 0.3),
            };
        case "onhold":
        case "draft":
            return {
                bg: alpha(theme.palette.warning.main, 0.08),
                color: theme.palette.warning.dark || theme.palette.warning.main,
                border: alpha(theme.palette.warning.main, 0.3),
            };
        case "closed":
            return {
                bg: alpha(theme.palette.error.main, 0.08),
                color: theme.palette.error.dark || theme.palette.error.main,
                border: alpha(theme.palette.error.main, 0.3),
            };
        default:
            return {
                bg: alpha(theme.palette.text.secondary, 0.05),
                color: theme.palette.text.secondary,
                border: alpha(theme.palette.divider, 0.8),
            };
    }
};

export const Spinner = ({ color, size = 14 }) => (
    <Box
        sx={{
            width: size,
            height: size,
            borderRadius: "50%",
            border: `2px solid ${alpha(color, 0.15)}`,
            borderTopColor: color,
            animation: "spin 0.75s linear infinite",
            "@keyframes spin": { "100%": { transform: "rotate(360deg)" } },
        }}
    />
);

const JobCard = ({
    item,
    viewingJdId,
    openingSkillsId,
    onView,
    onApply,
    onMatch,
    onSkills,
    onQuestions,
    onStatusChange,
}) => {
    const theme = useTheme();
    const ttProps = useTooltipProps(theme);
    const getStatusStyle = useStatusStyle(theme);
    const { applyBtnSx, matchBtnSx, skillsBtnSx, questionsBtnSx } = useActionButtonStyles(theme);

    const [anchorEl, setAnchorEl] = React.useState(null);
    const [statusLoading, setStatusLoading] = React.useState(false);

    const statusStyle = getStatusStyle(item?.status || "open");
    const isViewingThis = viewingJdId === item?.id;
    const isOpeningSkills = openingSkillsId === item?.id;
    const hasApplicants = (item?.applications || 0) > 0;

    const isApplyDisabled = ["onhold", "closed"].includes(item?.status?.toLowerCase());
    const isSkillsDisabled = item?.status?.toLowerCase() === "closed";

    // --- Dynamic Tooltip Logic For Skills Button ---
    let skillsTooltipMessage = "";
    if (isSkillsDisabled) {
        skillsTooltipMessage = "Skills cannot be modified for a closed JD.";
    } else if (hasApplicants) {
        skillsTooltipMessage = `Skills cannot be viewed because a CV/Candidate has already been applied to ${item?.job_title ? `"${item.job_title}"` : "this Job Description"}.`;
    }

    /* ---------------- STATUS LOGIC ---------------- */
    const handleStatusClick = (event) => {
        event.stopPropagation(); // Stop triggering onView card click
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (event) => {
        if (event) event.stopPropagation();
        setAnchorEl(null);
    };

    const handleStatusSelect = async (status, event) => {
        if (event) event.stopPropagation();
        handleClose();

        if (!onStatusChange) return;

        try {
            setStatusLoading(true);
            await onStatusChange(item, status);
        } catch (error) {
            console.error("Failed to transition job status:", error);
        } finally {
            setStatusLoading(false);
        }
    };

  return (
    <Box
        onClick={() => onView && onView(item)}
        sx={{
            display: "flex",
            flexDirection: "column",
            bgcolor: theme.palette.background.paper,
            borderRadius: "14px",
            p: 2,
            border: "1.5px solid",
            borderColor: isViewingThis ? theme.palette.primary.main : theme.palette.divider,
            position: "relative",
            cursor: onView ? "pointer" : "default",
            transition: "all 0.25s ease",
            boxShadow: isViewingThis
                ? `0 8px 24px -4px ${alpha(theme.palette.primary.main, 0.16)}`
                : "0 2px 10px -2px rgba(0,0,0,0.04)",
            "&:hover": {
                transform: "translateY(-2px)",
                borderColor: isViewingThis
                    ? theme.palette.primary.main
                    : alpha(theme.palette.primary.main, 0.35),
                boxShadow: isViewingThis
                    ? `0 12px 28px -4px ${alpha(theme.palette.primary.main, 0.2)}`
                    : `0 8px 20px -4px ${alpha(theme.palette.text.primary, 0.07)}`,
            },
        }}
    >
        {/* Header Row: Title + Status */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 1.5 }}>
            <Typography
                fontWeight={700}
                fontSize="0.9rem"
                color="text.primary"
                sx={{
                    lineHeight: 1.35,
                    letterSpacing: "-0.01em",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrientation: "vertical",
                    overflow: "hidden",
                    flexGrow: 1,
                    minWidth: 0,
                }}
            >
                {item?.job_title || "Untitled Position"}
            </Typography>

            {/* Container for Eye button and Status Chip */}
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                <Tooltip title="View Job Description" placement="top" {...ttProps}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onView && onView(item);
                            }}
                            disabled={viewingJdId !== null || openingSkillsId !== null}
                            sx={{
                                color: theme.palette.text.secondary,
                                p: 0.25,
                                "&:hover": {
                                    color: theme.palette.primary.main,
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                },
                            }}
                        >
                            {viewingJdId === item?.id ? (
                                <Spinner color={theme.palette.primary.main} size={11} />
                            ) : (
                                <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip title="Change job status" {...ttProps}>
                    <Chip
                        label={
                            statusLoading ? (
                                <Spinner color={statusStyle.color} size={10} />
                            ) : (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                                    <span>{item?.status || "open"}</span>
                                    <DropdownIcon sx={{ fontSize: "11px", opacity: 0.7 }} />
                                </Box>
                            )
                        }
                        onClick={handleStatusClick}
                        sx={{
                            cursor: "pointer",
                            flexShrink: 0,
                            bgcolor: statusStyle.bg,
                            color: statusStyle.color,
                            border: `1px solid ${statusStyle.border}`,
                            fontWeight: 800,
                            fontSize: "0.58rem",
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            height: "22px",
                            borderRadius: "5px",
                            "& .MuiChip-label": { px: 0.8 },
                            "&:hover": { bgcolor: alpha(statusStyle.color, 0.15) },
                        }}
                    />
                </Tooltip>
            </Stack>
        </Box>

        {/* Meta Info */}
        <Stack spacing={0.9} mb={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(theme.palette.text.secondary, 0.06), borderRadius: "5px", p: "3px" }}>
                    <LocationIcon sx={{ fontSize: "0.82rem", color: "text.secondary" }} />
                </Box>
                <Typography fontSize="0.73rem" fontWeight={500} noWrap color="text.secondary">
                    {item?.location || "Remote / Anywhere"}
                </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(theme.palette.info.main, 0.06), borderRadius: "5px", p: "3px" }}>
                    <PeopleAltIcon sx={{ fontSize: "0.82rem", color: "info.main" }} />
                </Box>
                <Typography fontSize="0.73rem" fontWeight={600} color="text.primary">
                    {item?.applications || 0}{" "}
                    <Typography component="span" fontSize="0.73rem" color="text.secondary" fontWeight={400}>
                        Applicants
                    </Typography>
                </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: "5px", p: "3px" }}>
                    <BusinessCenterIcon sx={{ fontSize: "0.82rem", color: "primary.main" }} />
                </Box>
                <Typography fontSize="0.73rem" fontWeight={500} noWrap color="text.secondary">
                    {item?.job_type || "Full-time"}
                </Typography>
            </Box>
        </Stack>

        {/* Action Buttons — equal width grid, no overflow */}
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "6px",
                pt: 1.5,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                mt: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Apply */}
            <Tooltip
                title={"JD is not open for applications"}
                placement="top"
                {...ttProps}
                disableHoverListener={!isApplyDisabled}
            >
                <span style={{ display: "flex", cursor: isApplyDisabled ? "not-allowed" : "inherit" }}>
                    <Button
                        sx={{
                            ...applyBtnSx,
                            width: "100%",
                            minWidth: 0,
                            px: 0,
                            fontSize: "0.68rem",
                            gap: "4px",
                            pointerEvents: isApplyDisabled ? "none" : "auto",
                        }}
                        disabled={isApplyDisabled}
                        onClick={() => onApply && onApply(item)}
                    >
                        Apply
                    </Button>
                </span>
            </Tooltip>

            {/* Match */}
            <Tooltip
                title={!hasApplicants ? "No applicants yet" : "Find matches"}
                placement="top"
                {...ttProps}
                disableHoverListener={hasApplicants}
            >
                <span style={{ display: "flex", cursor: !hasApplicants ? "not-allowed" : "inherit" }}>
                    <Button
                        sx={{
                            ...matchBtnSx,
                            width: "100%",
                            minWidth: 0,
                            px: 0,
                            fontSize: "0.68rem",
                            gap: "3px",
                            pointerEvents: !hasApplicants ? "none" : "auto",
                        }}
                        disabled={!hasApplicants}
                        onClick={() => onMatch && onMatch(item)}
                    >
                        Match
                    </Button>
                </span>
            </Tooltip>

            {/* Questions */}
            {/* <Tooltip
                title={"Skills cannot be modified for a closed JD"}
                placement="top"
                {...ttProps}
                disableHoverListener={!isSkillsDisabled}
            >
                <span style={{ display: "flex", cursor: isSkillsDisabled ? "not-allowed" : "inherit" }}>
                    <Button
                        sx={{
                            ...questionsBtnSx,
                            width: "100%",
                            minWidth: 0,
                            px: 0,
                            fontSize: "0.68rem",
                            gap: "3px",
                            pointerEvents: isSkillsDisabled ? "none" : "auto",
                        }}
                        disabled={isSkillsDisabled}
                        onClick={() => onQuestions && onQuestions(item)}
                    >
                        Que
                    </Button>
                </span>
            </Tooltip> */}

            {/* Skills */}
            <Tooltip
                title={skillsTooltipMessage}
                placement="top"
                {...ttProps}
                disableHoverListener={!skillsTooltipMessage}
            >
                <span style={{ display: "flex", cursor: skillsTooltipMessage ? "not-allowed" : "inherit" }}>
                    <Button
                        sx={{
                            ...skillsBtnSx,
                            width: "100%",
                            minWidth: 0,
                            px: 0,
                            fontSize: "0.68rem",
                            pointerEvents: skillsTooltipMessage ? "none" : "auto",
                        }}
                        onClick={() => onSkills && onSkills(item)}
                        disabled={isSkillsDisabled || isOpeningSkills || hasApplicants}
                    >
                        {isOpeningSkills
                            ? <Spinner color={theme.palette.warning.dark} size={10} />
                            : "Skills"
                        }
                    </Button>
                </span>
            </Tooltip>
        </Box>

        {/* Status Menu */}
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            onClick={(e) => e.stopPropagation()}
            elevation={0}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
                sx: {
                    mt: 0.75,
                    boxShadow: "0 8px 20px -4px rgba(0,0,0,0.1)",
                    borderRadius: "10px",
                    border: `1.5px solid ${theme.palette.divider}`,
                    minWidth: "120px",
                    p: 0.4,
                    "& .MuiMenuItem-root": {
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        py: 0.8,
                        px: 1.2,
                        borderRadius: "6px",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        my: 0.15,
                        "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            color: theme.palette.primary.main,
                        },
                    },
                },
            }}
        >
            {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} onClick={(e) => handleStatusSelect(status, e)}>
                    {status}
                </MenuItem>
            ))}
        </Menu>
    </Box>
  );
};

export default JobCard;

